import { Injectable } from '@angular/core';
import { LineChart } from '@client/app/modules/charts/models/charts/line';
import { ChartsDataHelper } from '@client/app/blocks/helpers/charts/charts-data-helper';
import MonColors from '@monsido/colors/dist/es6/colors';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { AccessibilityResultTypeService } from '../../accessibility/result-type.service';
import { DateTimeService } from '@client/app/services/date-time/date-time.service';
import { AccessibilityGuidelinesInfoService } from '@client/app/modules/accessibility/services/mon-accessibility-guidelines/accessibility-guideline-info.service';
import { AccessibilityGuidelineVersionService } from '@monsido/ng2/modules/accessibility/services/accessibility-guideline-version.service';
import { LegendCursorService } from '@client/deprecate/frontend-components/src/js/components/charts/services/LegendCursor.service';
import { MonNumberService } from '@monsido/ng2/core/locale/services/mon-number.service';
import { ChartDataEntry, ChartOptions, ChartVerticalLines } from '../base-chart/chart-factory';
import { CrawlHistory } from '@monsido/ng2/modules/models/api/interfaces/domain.interface';
import { AccessibilityGuidelineInterface } from '@client/app/modules/accessibility/interfaces/acc-info-interface';

type AccessibilityLineChartComputedCrawlHistory = CrawlHistory & {
    accessibility_checks_in_compliance?: number;
    accessibility_checks_in_error?: number;
};

@Injectable({
    providedIn: 'root',
})
export class AccessibilityLineChartServiceFactory {
    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private accessibilityResultTypeService: AccessibilityResultTypeService,
        private dateTimeService: DateTimeService,
        private accessibilityGuidelinesInfoService: AccessibilityGuidelinesInfoService,
        private accessibilityGuidelineVersionService: AccessibilityGuidelineVersionService,
        private legendCursorService: LegendCursorService,
        private monNumberService: MonNumberService,
    ) {}

    createInstance () {
        return new AccessibilityLineChartService(
            this.translateService,
            this.sessionService,
            this.accessibilityResultTypeService,
            this.dateTimeService,
            this.accessibilityGuidelinesInfoService,
            this.accessibilityGuidelineVersionService,
            this.legendCursorService,
            this.monNumberService,
        );
    }
}
export class AccessibilityLineChartService {
    private lineChartSettings: LineChart = new LineChart();
    private accessibilityType: string | null | undefined = undefined;
    private selectedGroup: string | undefined = undefined;
    private accessibilityChecks: any = {};

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private accessibilityResultTypeService: AccessibilityResultTypeService,
        private dateTimeService: DateTimeService,
        private accessibilityGuidelinesInfoService: AccessibilityGuidelinesInfoService,
        private accessibilityGuidelineVersionService: AccessibilityGuidelineVersionService,
        private legendCursorService: LegendCursorService,
        private monNumberService: MonNumberService,
    ) {}

    init (accessibilityType?: string, selectedGroup?: string): void {
        this.lineChartSettings = new LineChart();

        if (accessibilityType) {
            this.accessibilityType = accessibilityType;
        } else if (this.sessionService.domain !== null) {
            this.accessibilityType = this.sessionService.domain.features.accessibility;
        }

        this.selectedGroup = selectedGroup;
        this.setChecks();
    }

    setup (): Promise<AccessibilityGuidelineInterface[]> {
        const abbrs = this.accessibilityType ? [this.accessibilityType] : [];
        return this.accessibilityGuidelinesInfoService.get(abbrs);
    }

    getOption (resultType: string, crawls: any[]): Promise<ChartOptions | null> {
        const abbrs = crawls.map((crawl) => crawl.accessibility_guideline) || [];

        if (abbrs.length === 0) {
            return Promise.resolve(null);
        }

        return this.accessibilityGuidelinesInfoService.get(abbrs).then(() => {
            const mappedCrawls = crawls.map((crawl) => {
                return this.accessibilityResultTypeService.getCheckCountByResultType(
                    resultType,
                    crawl.accessibility_guideline,
                );
            });

            const checkCount = Math.max.apply(undefined, mappedCrawls);
            const showIssues = this.selectedGroup === 'issues';

            this.lineChartSettings.setTooltipSettings({
                intersect: true,
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        const result = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index].y;
                        const label = data.datasets[tooltipItem.datasetIndex].label;
                        return label + ': ' + this.monNumberService.transformNumber(result, '0,0');
                    },
                    title: (tooltipItem: any) => {
                        return this.dateTimeService.format(tooltipItem[0].xLabel, 'MMM DD');
                    },
                },
            });

            this.lineChartSettings.setLegendSettingsOverride({
                display: true,
                position: 'bottom',
                labels: {
                    fontSize: 11,
                    boxWidth: 10,
                },
            });

            this.lineChartSettings.setScalesSettings({
                xAxes: [
                    {
                        id: 'x-axis-0',
                        type: 'time',
                        stacked: true,
                        gridLines: {
                            drawBorder: false,
                            display: false,
                        },
                        ticks: {
                            fontSize: 11,
                            maxTicksLimit: 10,
                        },
                        time: {
                            minUnit: 'day',
                            displayFormats: {
                                millisecond: 'MMM D',
                                second: 'MMM D',
                                minute: 'MMM D',
                                hour: 'MMM D',
                                day: 'MMM D',
                                week: 'MMM D',
                                month: 'MMM D',
                                quarter: 'MMM D',
                                year: 'MMM D',
                            },
                        },
                    },
                ],
                yAxes: [
                    {
                        id: 'y-axis-0',
                        position: 'right',
                        display: !showIssues,
                        ticks: {
                            min: 0,
                            beginAtZero: true,
                            maxTicksLimit: 4,
                            callback: (value: any) => {
                                return this.monNumberService.transformNumber(value, '0,0a').toUpperCase();
                            },
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.translateService.getString('Pages'),
                            fontSize: 11,
                        },
                        afterDataLimits: (axis: any) => {
                            axis.options.display = !showIssues && axis.max > 1;
                        },
                    },
                    {
                        id: 'y-axis-1',
                        stacked: true,
                        ticks: {
                            min: 0,
                            beginAtZero: true,
                            maxTicksLimit: 4,
                            stepSize: Math.ceil(checkCount / 4),
                            max: checkCount,
                            callback: (value: any) => {
                                return this.monNumberService.transformNumber(value, '0,0');
                            },
                        },
                        gridLines: {
                            drawBorder: false,
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.translateService.getString('Checks'),
                            fontSize: 11,
                        },
                    },
                    {
                        id: 'y-axis-2',
                        position: 'right',
                        display: showIssues,
                        ticks: {
                            min: 0,
                            beginAtZero: true,
                            maxTicksLimit: 4,
                            callback: (value: any) => {
                                return this.monNumberService.transformNumber(value, '0,0a').toUpperCase();
                            },
                        },
                        scaleLabel: {
                            display: true,
                            labelString: this.translateService.getString('Issues'),
                            fontSize: 11,
                        },
                        afterDataLimits: (axis: any) => {
                            axis.options.display = showIssues && axis.max > 1;
                        },
                    },
                ],
            });

            this.lineChartSettings.options = this.legendCursorService.setHoverState(this.lineChartSettings.options);

            if (crawls) {
                return this.accessibilityGuidelineVersionService
                    .getVersions(crawls)
                    .then((versionChanges) => {
                        const lines: ChartVerticalLines[] = [];
                        for (let i = 0; i < crawls.length; i++) {
                            const element = crawls[i];
                            for (let j = 0; j < versionChanges.length; j++) {
                                const versionChange = versionChanges[j];
                                if (element.post_processing_done_at === versionChange.time) {
                                    lines.push({
                                        xAxisID: 'x-axis-0',
                                        x: element.date,
                                        style: {
                                            lineDash: [5, 5],
                                            color: MonColors['secondary-15'],
                                            width: 3,
                                        },
                                        label: {
                                            text: versionChange.title,
                                            color: MonColors['secondary-15'],
                                            position: 'top',
                                        },
                                        data: {
                                            link: versionChange.infoLink,
                                        },
                                    });
                                    break;
                                }
                            }
                        }

                        this.lineChartSettings.options.verticalLines = {
                            lines: lines,
                        };

                        return this.lineChartSettings.options;
                    })
                    .catch(() => this.lineChartSettings.options);
            } else {
                return this.lineChartSettings.options;
            }
        });
    }

    getColors (): string[] {
        const colors = {
            A: MonColors['secondary-12'],
            AA: MonColors['secondary-13'],
            AAA: MonColors['secondary-14'],
        };

        if (this.selectedGroup === 'issues') {
            this.lineChartSettings.addColor('#ff101a');
        } else {
            for (const key of Object.keys(this.accessibilityChecks)) {
                this.lineChartSettings.addColor(colors[key]);
            }
        }

        this.lineChartSettings.addColors(['#57b2b7', '']);
        this.lineChartSettings.addColors(['#ff9d4e', '#a9dad6']);

        return this.lineChartSettings.colors;
    }

    getSeries (): string[] {
        const series = {
            A: this.translateService.getString('A'),
            AA: this.translateService.getString('AA'),
            AAA: this.translateService.getString('AAA'),
        };

        if (this.selectedGroup === 'issues') {
            this.lineChartSettings.addSerie(
                this.translateService.getString('Accessibility issues'),
            );
        } else {
            for (const key of Object.keys(this.accessibilityChecks)) {
                this.lineChartSettings.addSerie(series[key]);
            }
        }

        this.lineChartSettings.addSeries([
            this.translateService.getString('Checks passed'),
            this.translateService.getString('Failing checks'),
            this.translateService.getString('Pages with issues'),
            this.translateService.getString('Scanned pages'),
        ]);

        return this.lineChartSettings.series;
    }

    getOverride (): any[] {
        if (this.selectedGroup === 'issues') {
            this.lineChartSettings.addOverride({
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-2',
                type: 'line',
                fill: false,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                pointHitRadius: 34,
            });
        } else {
            Object.keys(this.accessibilityChecks).forEach(() => {
                this.lineChartSettings.addOverride({
                    xAxisID: 'x-axis-0',
                    yAxisID: 'y-axis-1',
                    type: 'line',
                    fill: false,
                    lineTension: 0,
                    radius: 0,
                    borderWidth: 1,
                    pointRadius: 3,
                    pointHitRadius: 34,
                });
            });
        }

        this.lineChartSettings.addOverrides([
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'bar',
                borderWidth: 0,
                backgroundColor: '#57b2b7',
                borderColor: '#57b2b7',
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-1',
                type: 'bar',
                borderWidth: 0,
                backgroundColor: '#eee',
                borderColor: '#eee',
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-0',
                type: 'line',
                fill: true,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                borderDash: [5, 5],
                pointHitRadius: 34,
            },
            {
                xAxisID: 'x-axis-0',
                yAxisID: 'y-axis-0',
                type: 'line',
                fill: true,
                lineTension: 0,
                radius: 0,
                borderWidth: 1,
                pointRadius: 3,
                borderDash: [5, 5],
                pointHitRadius: 34,
            },
        ]);

        return this.lineChartSettings.override;
    }

    getLabels (crawls: any[]): number[] {
        crawls = crawls || [];
        return crawls.map((crawl) => {
            return new Date(crawl.date).getTime();
        });
    }

    getData (crawls: AccessibilityLineChartComputedCrawlHistory[], resultType: string = ''): ChartDataEntry[][] {
        crawls = crawls || [];
        const helper = new ChartsDataHelper('time');
        const lineChart: ChartDataEntry[][] = [];

        const updatedCrawls: AccessibilityLineChartComputedCrawlHistory[] = crawls
            .filter((crawl, index) => {
                return (index === 0 || crawl.date !== crawls[index - 1]?.date) && crawl.accessibility_guideline;
            })
            .map((crawl) => {
                const resultTypeParams = this.accessibilityResultTypeService.getChecksParameters(
                    resultType,
                    crawl.accessibility_guideline || '',
                );
                const checkCount = this.accessibilityResultTypeService.getCheckCountByResultType(
                    resultType,
                    crawl.accessibility_guideline || '',
                );

                crawl.accessibility_checks = crawl.accessibility_checks || {
                    A: 0,
                    AA: 0,
                    error: 0,
                    warning: 0,
                    review: 0,
                };
                crawl.accessibility_checks.date = crawl.date;
                crawl.accessibility_checks_in_error = helper.getCount(
                    crawl.accessibility_checks as unknown as CrawlHistory,
                    resultTypeParams,
                );
                crawl.accessibility_checks_in_compliance =
          checkCount - crawl.accessibility_checks_in_error;

                return crawl;
            });

        if (this.selectedGroup === 'issues') {
            lineChart.push(
                updatedCrawls.map((crawl) => {
                    return helper.setData(crawl, 'accessiblity_issues_count');
                }),
            );
        } else {
            for (const i of Object.keys(this.accessibilityChecks)) {
                lineChart.push(
                    updatedCrawls.map((crawl) => {
                        return helper.setData(
                            {
                                result: this.accessibilityResultTypeService.getCrawlIssueCount(
                                    crawl,
                                    i,
                                    resultType,
                                ),
                                date: crawl.date,
                            },
                            'result',
                        );
                    }),
                );
            }
        }

        lineChart.push(
            updatedCrawls.map((crawl) => {
                return helper.setData(crawl, 'accessibility_checks_in_compliance');
            }),
        );

        lineChart.push(
            updatedCrawls.map((crawl) => {
                return helper.setData(crawl, 'accessibility_checks_in_error');
            }),
        );

        lineChart.push(
            updatedCrawls.map((crawl) => {
                return helper.setData(crawl, 'pages_with_accessibility_errors_count');
            }),
        );

        lineChart.push(
            updatedCrawls.map((crawl) => {
                return helper.setData(crawl, 'page_count');
            }),
        );

        return lineChart;
    }

    private setChecks (): void {
        this.accessibilityChecks = {};
        if (!this.accessibilityType) {
            return;
        }

        // WCAG2
        if (
            [
                'WCAG2-A',
                'WCAG2-AA',
                'WCAG2-AAA',
                'WCAG21-A',
                'WCAG21-AA',
                'WCAG21-AAA',
            ].indexOf(this.accessibilityType) > -1
        ) {
            this.accessibilityChecks.A = 0;
        }

        if (
            ['WCAG2-AA', 'WCAG2-AAA', 'WCAG21-AA', 'WCAG21-AAA'].indexOf(
                this.accessibilityType,
            ) > -1
        ) {
            this.accessibilityChecks.AA = 0;
        }

        if (['WCAG2-AAA', 'WCAG21-AAA'].indexOf(this.accessibilityType) > -1) {
            this.accessibilityChecks.AAA = 0;
        }
    }
}
